'use client';
import { useAtomValue } from 'jotai';
import { cartItemsQuantityAtom } from '@/store/cartStore';
import { formatPrice } from '@/sanity/utils/swell/priceHelpers';
import { testIds } from '@/constants/testIds';
import useCart from '@/hooks/useCart';
import { useState } from 'react';
import AnimatedSpinner from '@/components/Icons/AnimatedSpinner';
import Close from '@/components/Icons/Close';

// import { cartItemsQuantityAtom } from '@/scripts/store/cartStore';
export default function CartCostBreakdown({
  displayType,
}: {
  displayType: string;
}) {
  const [loading, setLoading] = useState(false);
  const { cart, clearDiscount } = useCart();
  const cartItemsQuantity = useAtomValue(cartItemsQuantityAtom);
  return (
    <>
      {cart && cart.totalPrice ? (
        <dl className='space-y-2 py-4'>
          {cart.discountCode && displayType === 'purchase-journey' && (
            <>
              <div className='flex items-center justify-between rounded-md bg-teal-50 p-3'>
                <div className='flex items-center space-x-2'>
                  <span className='text-sm font-medium text-teal-800'>
                    Discount
                  </span>
                  <span className='inline-flex items-center rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-teal-700 ring-1 ring-inset ring-teal-200'>
                    {cart.discountCode}
                  </span>
                </div>
                <div className='flex items-center space-x-3'>
                  {cart.discountAmount > 0 && (
                    <span className='text-sm font-medium text-teal-700'>
                      {formatPrice(-cart.discountAmount)}
                    </span>
                  )}
                  <button
                    onClick={async () => {
                      setLoading(true);
                      try {
                        await clearDiscount();
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading}
                    className={`flex items-center space-x-1 rounded-full px-2 py-1 text-xs font-medium transition ${
                      loading
                        ? 'cursor-not-allowed bg-teal-100 text-teal-400'
                        : 'bg-teal-100 text-teal-700 hover:bg-teal-200 hover:text-teal-900'
                    }`}
                    aria-label='Remove discount'
                  >
                    {loading ? <AnimatedSpinner /> : <Close />}
                    <span>{loading ? 'Removing...' : 'Remove'}</span>
                  </button>
                </div>
              </div>

              {/* 💬 Discount Note */}
              {cart.discountAmount === 0 && (
                <p className='mt-1 text-xs italic text-teal-700'>
                  Your discount code was applied, but it doesn’t apply to the
                  items currently in your cart.
                </p>
              )}
            </>
          )}

          <div className='flex items-center justify-between'>
            <dt>Subtotal</dt>
            <dd>{formatPrice(cart.subtotalPrice)}</dd>
          </div>
          <div className='flex items-center justify-between'>
            <dt>Delivery</dt>

            <dd>
              {displayType === 'purchase-journey'
                ? cart.deliveryPrice > 0
                  ? formatPrice(cart.deliveryPrice)
                  : 'FREE'
                : cart.deliveryPrice > 0
                  ? formatPrice(cart.deliveryPrice)
                  : 'FREE'}
            </dd>
          </div>
          <div className='flex items-center justify-between'>
            <dt>VAT</dt>
            <dd>{formatPrice(cart.totalTax)}</dd>
          </div>

          <div className='flex items-center justify-between pt-2'>
            <dt className='text-lg font-bold'>
              <>
                Total:{' '}
                <>
                  ({cartItemsQuantity}{' '}
                  {cartItemsQuantity > 1 ? 'items' : 'item'})
                </>
              </>
            </dt>
            <dd
              className='text-lg font-bold'
              data-test-qa={testIds.purchaseJourneyTotal}
            >
              {formatPrice(cart.totalPrice)}
            </dd>
          </div>
        </dl>
      ) : (
        ''
      )}
    </>
  );
}
